import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock } from "lucide-react";
import { Seo, SITE_URL } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { getPost } from "../lib/api";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    getPost(slug)
      .then(setPost)
      .catch(() => setMissing(true));
  }, [slug]);

  const jsonLd = post
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: post.title,
        datePublished: post.date,
        author: { "@type": "Organization", name: "OneCare Pharmacy" },
        url: `${SITE_URL}/blog/${post.slug}`,
      }
    : null;

  return (
    <main className="bg-[#F8FAFC]">
      {post && (
        <Seo title={`${post.title} | OneCare Pharmacy Blog`} description={post.excerpt} path={`/blog/${post.slug}`} jsonLd={jsonLd} />
      )}

      {missing ? (
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-4 px-5 py-32 text-center" data-testid="blog-post-missing">
          <p className="font-display text-2xl font-bold text-slate-900">Article not found</p>
          <p className="text-sm text-slate-500">This article may have been moved or removed.</p>
          <Link to="/blog" data-testid="blog-back-from-missing" className="rounded-full bg-[#0D5C46] px-6 py-3 text-sm font-semibold text-white hover:bg-[#0A4837]">
            Back to blog
          </Link>
        </div>
      ) : !post ? (
        <p className="py-32 text-center text-sm text-slate-400" data-testid="blog-post-loading">
          Loading article...
        </p>
      ) : (
        <article className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
          <Reveal>
            <Link
              to="/blog"
              data-testid="blog-back-link"
              className="group inline-flex items-center gap-2 font-display text-sm font-bold text-emerald-800 transition-[color] hover:text-emerald-600"
            >
              <ArrowLeft className="h-4 w-4 transition-[transform] group-hover:-translate-x-1" />
              All articles
            </Link>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <span className="rounded-full bg-[#D8EFE6] px-3 py-1 font-mono text-[11px] font-medium text-emerald-800">
                {post.category}
              </span>
              <span className="font-mono text-xs text-slate-400">{post.date}</span>
              <span className="flex items-center gap-1.5 font-mono text-xs text-slate-400">
                <Clock className="h-3.5 w-3.5" />
                {post.read_time}
              </span>
            </div>
            <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight text-slate-900 sm:text-4xl">
              {post.title}
            </h1>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-10 space-y-6" data-testid="blog-post-content">
              {post.content.map((para, i) => (
                <p key={i} className={`leading-relaxed text-slate-600 ${i === 0 ? "text-lg text-slate-700" : "text-base"}`}>
                  {para}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-14 rounded-2xl bg-[#0D5C46] p-8 text-white sm:p-10">
              <h2 className="font-display text-xl font-bold sm:text-2xl">Questions about a product?</h2>
              <p className="mt-2 text-sm leading-relaxed text-emerald-100/85">
                Our team answers every enquiry by email — product information, availability and order questions.
              </p>
              <Link
                to="/contact"
                data-testid="blog-post-contact-cta"
                className="mt-6 inline-block rounded-full bg-white px-6 py-3 font-display text-sm font-bold text-[#0D5C46] transition-[background-color] hover:bg-emerald-200"
              >
                Make an Enquiry
              </Link>
            </div>
          </Reveal>
        </article>
      )}
    </main>
  );
}
